import { makeAutoObservable, runInAction } from 'mobx';
import { calendarEventApi } from '../api/calendar_event_api';
import type { CalendarEvent, CalendarListItem } from '../types/calendar';

export class CalendarEventStore {
  events: Map<string, CalendarEvent> = new Map();
  syncedCalendars: CalendarListItem[] = [];
  hasCalendarInfo: { hasCalendar: boolean; timezone?: string; preferences?: string } | null = null;
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Get all events as array
   */
  get eventList(): CalendarEvent[] {
    return Array.from(this.events.values());
  }

  /**
   * Fetch calendar events for a date range
   */
  async fetchEvents(
    associatedType: string,
    associatedUuid: string,
    start: string,
    end: string
  ): Promise<CalendarEvent[]> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const events = await calendarEventApi.list({
        associated_type: associatedType,
        associated_uuid: associatedUuid,
        start,
        end,
      });

      runInAction(() => {
        // Add key property and store events
        events.forEach((event) => {
          event.key = event.id;
          this.events.set(event.id, event);
        });
        this.loading = false;
      });

      return events;
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Failed to fetch events';
      });
      return [];
    }
  }

  /**
   * Create a new calendar event
   */
  async createEvent(
    associatedType: string,
    associatedUuid: string,
    event: CalendarEvent
  ): Promise<CalendarEvent | null> {
    try {
      const created = await calendarEventApi.create({
        associated_type: associatedType,
        associated_uuid: associatedUuid,
        summary: event.summary,
        description: event.description,
        calendar_id: event.calendar?.id || '',
        location: event.location,
        start: event.start?.dateTime || '',
        end: event.end?.dateTime || '',
      });

      runInAction(() => {
        created.key = created.id;
        created.associated_type = associatedType;
        created.associated_uuid = associatedUuid;
        this.events.set(created.id, created);
      });

      return created;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to create event';
      });
      return null;
    }
  }

  /**
   * Update an existing calendar event
   */
  async updateEvent(
    associatedType: string,
    associatedUuid: string,
    event: CalendarEvent
  ): Promise<CalendarEvent | null> {
    try {
      const updated = await calendarEventApi.update({
        associated_type: associatedType,
        associated_uuid: associatedUuid,
        id: event.id,
        summary: event.summary,
        description: event.description,
        location: event.location,
        start: event.start?.dateTime || '',
        end: event.end?.dateTime || '',
      });

      runInAction(() => {
        updated.key = updated.id;
        this.events.set(updated.id, updated);
      });

      return updated;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to update event';
      });
      return null;
    }
  }

  /**
   * Delete a calendar event
   */
  async deleteEvent(
    associatedType: string,
    associatedUuid: string,
    eventId: string,
    calendarId: string
  ): Promise<boolean> {
    try {
      await calendarEventApi.delete({
        associated_type: associatedType,
        associated_uuid: associatedUuid,
        event_id: eventId,
        calendar_id: calendarId,
      });

      runInAction(() => {
        this.events.delete(eventId);
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to delete event';
      });
      return false;
    }
  }

  /**
   * Fetch synced calendars for association
   */
  async fetchSyncedCalendars(
    associatedType: string,
    associatedUuid: string
  ): Promise<CalendarListItem[]> {
    try {
      const calendars = await calendarEventApi.getSyncedCalendars({
        associated_type: associatedType,
        associated_uuid: associatedUuid,
      });

      runInAction(() => {
        this.syncedCalendars = calendars;
      });

      return calendars;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to fetch synced calendars';
      });
      return [];
    }
  }

  /**
   * Check if user has calendar connected
   */
  async checkHasCalendar(
    associatedType: string,
    associatedUuid: string
  ): Promise<boolean> {
    try {
      const result = await calendarEventApi.hasCalendar({
        associated_type: associatedType,
        associated_uuid: associatedUuid,
      });

      runInAction(() => {
        this.hasCalendarInfo = result;
      });

      return result.hasCalendar;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to check calendar status';
        this.hasCalendarInfo = { hasCalendar: false };
      });
      return false;
    }
  }

  /**
   * Remove a temporary/fake event from the store
   */
  removeFakeEvent(eventId: string): void {
    if (eventId.startsWith('FAKE://')) {
      this.events.delete(eventId);
    }
  }

  clearError(): void {
    this.error = null;
  }

  clearEvents(): void {
    this.events.clear();
  }

  reset(): void {
    this.events.clear();
    this.syncedCalendars = [];
    this.hasCalendarInfo = null;
    this.loading = false;
    this.error = null;
  }
}

export const calendarEventStore = new CalendarEventStore();
